import React, { useEffect, useState } from "react";
import { api, formatRupiah, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const defaultsFor = (fields) => {
  const o = {};
  fields.forEach(f => {
    if (f.type === "checkbox") o[f.name] = false;
    else if (f.type === "number") o[f.name] = 0;
    else if (f.type === "json") o[f.name] = "[]";
    else if (f.type === "lines") o[f.name] = "";
    else o[f.name] = "";
  });
  return o;
};

const formToPayload = (form, fields) => {
  const payload = {};
  fields.forEach(f => {
    let v = form[f.name];
    if (f.type === "number") v = v === "" || v === null ? null : Number(v);
    else if (f.type === "json") {
      try { v = JSON.parse(v || "[]"); } catch { v = []; }
    }
    else if (f.type === "lines") {
      v = (v || "").split("\n").map(s => s.trim()).filter(Boolean);
    }
    payload[f.name] = v;
  });
  return payload;
};

const itemToForm = (item, fields) => {
  const form = {};
  fields.forEach(f => {
    let v = item?.[f.name];
    if (f.type === "json") v = v ? JSON.stringify(v, null, 2) : "[]";
    else if (f.type === "lines") v = Array.isArray(v) ? v.join("\n") : "";
    else if (v === undefined || v === null) v = f.type === "checkbox" ? false : (f.type === "number" ? 0 : "");
    form[f.name] = v;
  });
  return form;
};

export default function AdminCrud({ name, label, fields }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultsFor(fields));

  const load = () => api.get(`/admin/${name}`).then(r => setItems(r.data));
  useEffect(() => { load(); }, [name]);

  const startNew = () => { setEditing(null); setForm(defaultsFor(fields)); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm(itemToForm(it, fields)); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = formToPayload(form, fields);
    try {
      if (editing) await api.put(`/admin/${name}/${editing.id}`, payload);
      else await api.post(`/admin/${name}`, payload);
      toast.success("Tersimpan");
      setOpen(false); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Yakin hapus?")) return;
    try { await api.delete(`/admin/${name}/${id}`); load(); toast.success("Dihapus"); }
    catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const handleUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    const toastId = toast.loading("Mengunggah...");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, [fieldName]: data.url });
      toast.success("Berhasil diunggah", { id: toastId });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail), { id: toastId });
    }
  };

  const displayCol = fields[0]?.name || "id";

  return (
    <div data-testid={`admin-${name}-page`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl text-[#1A1728]">{label}</h1>
          <p className="text-[#5C5870] mt-1">{items.length} item</p>
        </div>
        <button onClick={startNew} className="lumina-btn-primary" data-testid={`add-${name}-btn`}><Plus className="w-4 h-4" /> Tambah</button>
      </div>

      <div className="lumina-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F1EBFA]/40 text-[#5C5870] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-6 py-4">{displayCol}</th>
              {fields.find(f=>f.name === "price") && <th className="text-left px-6 py-4">Harga</th>}
              {fields.find(f=>f.name === "is_active" || f.name === "featured" || f.name === "is_featured") && <th className="text-left px-6 py-4">Status</th>}
              <th className="text-right px-6 py-4">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-[#E2DFED] hover:bg-[#F1EBFA]/20">
                <td className="px-6 py-4 font-medium text-[#1A1728]">{it[displayCol]}</td>
                {fields.find(f=>f.name === "price") && <td className="px-6 py-4 text-[#6B4BA3]">{formatRupiah(it.price)}</td>}
                {fields.find(f=>f.name === "is_active" || f.name === "featured" || f.name === "is_featured") && (
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs ${it.is_active === false ? "bg-gray-100 text-gray-600" : "bg-green-100 text-green-700"}`}>
                      {it.is_active === false ? "Inactive" : "Active"}
                    </span>
                  </td>
                )}
                <td className="px-6 py-4 text-right">
                  <button onClick={() => startEdit(it)} className="text-[#6B4BA3] hover:bg-[#E8F0FA] p-2 rounded-lg" data-testid={`edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(it.id)} className="text-[#E57373] hover:bg-red-50 p-2 rounded-lg ml-1" data-testid={`del-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit" : "Tambah"} {label}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {fields.map(f => (
              <div key={f.name}>
                <label className="lumina-label block mb-2">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea value={form[f.name] ?? ""} onChange={e => setForm({...form, [f.name]: e.target.value})} rows={3} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" />
                ) : (f.name === "image" || f.name === "avatar" || f.name === "logo" || f.type === "image") ? (
                  <div className="space-y-3">
                    {form[f.name] && <img src={form[f.name].startsWith("/") ? (process.env.REACT_APP_BACKEND_URL || "") + form[f.name] : form[f.name]} alt="Preview" className="h-32 object-contain rounded border border-[#E2DFED] p-2" />}
                    <input type="file" accept="image/*" onChange={(e) => handleUpload(e, f.name)} className="w-full text-sm file:mr-4 file:py-2.5 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#F1EBFA] file:text-[#6B4BA3] hover:file:bg-[#E8F0FA] cursor-pointer border border-[#E2DFED] rounded-xl p-2" />
                    <input type="hidden" value={form[f.name] ?? ""} />
                  </div>
                ) : f.type === "json" ? (
                  <textarea value={form[f.name] ?? ""} onChange={e => setForm({...form, [f.name]: e.target.value})} rows={6} placeholder={f.placeholder} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none font-mono text-xs" />
                ) : f.type === "lines" ? (
                  <textarea value={form[f.name] ?? ""} onChange={e => setForm({...form, [f.name]: e.target.value})} rows={4} placeholder="Satu item per baris" className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" />
                ) : f.type === "checkbox" ? (
                  <input type="checkbox" checked={!!form[f.name]} onChange={e => setForm({...form, [f.name]: e.target.checked})} className="w-5 h-5 accent-[#6B4BA3]" />
                ) : f.type === "select" ? (
                  <select value={form[f.name] ?? ""} onChange={e => setForm({...form, [f.name]: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none">
                    <option value="">— pilih —</option>
                    {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type || "text"} value={form[f.name] ?? ""} onChange={e => setForm({...form, [f.name]: e.target.value})} className="w-full px-4 py-2.5 rounded-xl border border-[#E2DFED] focus:border-[#6B4BA3] outline-none" />
                )}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="lumina-btn-outline">Batal</button>
              <button type="submit" className="lumina-btn-primary" data-testid={`save-${name}-btn`}>Simpan</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
